import { type FC } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify/unstyled';

import { Form, FormControl, FormField, FormItem, FormMessage, Input } from '@shared/components/ui';
import { GenericButton, GenericModal } from '@shared/components/ui/custom';
import type { RTKQueryError } from '@shared/utils/api.types';

import { useGetResetPasswordLinkMutation } from '../../api/authentication.api';

import { type ForgotPasswordFormValues, ForgotPasswordSchema } from './schemas/forgot-password.schema';

export const ForgotPasswordModal: FC<{ onClose: () => void }> = ({ onClose }) => {
  const [getResetPasswordLink, { isLoading, reset }] = useGetResetPasswordLinkMutation();

  const form = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(ForgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  });

  const handleClose = () => {
    onClose();
    reset?.();
  };

  const onSubmit = async (data: ForgotPasswordFormValues) => {
    try {
      await getResetPasswordLink({ email: data.email }).unwrap();
      toast.success('Reset password link sent. Please check your email.');
      handleClose();
    } catch (err) {
      const errorObj = err as RTKQueryError;

      const backendErrors = errorObj?.data?.errors ?? {};

      if (Object.keys(backendErrors).length > 0) {
        Object.entries(backendErrors).forEach(([key, message]) => {
          form.setError(key as keyof ForgotPasswordFormValues, {
            type: 'server',
            message: message,
          });
        });
      } else {
        const globalMessage = errorObj?.message ?? 'Error sending reset password link.';
        toast.error(globalMessage);
      }
    }
  };

  return (
    <GenericModal
      open
      onOpenChange={handleClose}
      title="Reset User Password"
      description="Please enter an email. A reset password link will be sent to this email."
      data-testid="authentication-forgot-modal"
    >
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-4 pt-4"
          noValidate
          data-testid="authentication-forgot-modal-content"
        >
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    {...field}
                    type="email"
                    placeholder="Enter email"
                    data-testid="authentication-forgot-input-email"
                  />
                </FormControl>
                <FormMessage data-testid="authentication-forgot-error-email" />
              </FormItem>
            )}
          />

          <div className="flex justify-end space-x-2 pt-2" data-testid="authentication-forgot-modal-actions">
            <GenericButton
              variant="outline"
              label="Cancel"
              type="button"
              onClick={handleClose}
              data-testid="authentication-forgot-cancel-btn"
            />

            <GenericButton
              label="Reset"
              type="submit"
              isLoading={isLoading}
              data-testid="authentication-forgot-reset-btn"
            />
          </div>
        </form>
      </Form>
    </GenericModal>
  );
};
